import classNames from 'classnames';
import { ComponentProps, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import logError from '@alltrails/core/utils/logError';
import { DropdownMenuItems } from '@alltrails/denali/components/DropdownMenu';
import IconButton from '@alltrails/denali/components/IconButton';
import Check from '@alltrails/denali/icons/Check';
import Copy from '@alltrails/denali/icons/Copy';
import type { ClusteredPOIResult } from '@alltrails/types/AlgoliaResults';
import PaddingAwarePopup from '../../PaddingAwarePopup';
import { getPOIPopupOffset } from '../POIResults/getPOIPopupOffset';
import styles from './ActivePOIPopupOptions.module.scss';

type ActivePOIPopupOptionsProps = {
  options: ComponentProps<typeof DropdownMenuItems>['options'];
  poi: ClusteredPOIResult;
  isTapAnywhereEnabled?: boolean;
  onCopyCoordinates?: () => void;
};

const ActivePOIPopupOptions = ({ options, poi, isTapAnywhereEnabled = false, onCopyCoordinates }: ActivePOIPopupOptionsProps) => {
  const { _geoloc, clusterCoordinates, photos_count, name, type_label } = poi;
  const [isCopied, setIsCopied] = useState(false);
  const intl = useIntl();

  const onClickCopyCoordinates = useCallback(() => {
    if (!_geoloc) {
      return;
    }
    onCopyCoordinates?.();
    navigator.clipboard
      .writeText(`${_geoloc.lat.toFixed(4)}, ${_geoloc.lng.toFixed(4)}`)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1500);
      })
      .catch(err => {
        logError(new Error('Failed to copy coordinates to clipboard', { cause: err }));
      });
  }, [_geoloc, onCopyCoordinates]);

  return (
    <PaddingAwarePopup
      closeButton={false}
      closeOnClick={false}
      focusAfterOpen={false}
      latitude={clusterCoordinates?.lat ?? _geoloc!.lat}
      longitude={clusterCoordinates?.lng ?? _geoloc!.lng}
      maxWidth="327"
      offset={getPOIPopupOffset(!!photos_count)}
      preferTopAnchor
    >
      <div className={classNames(styles.container, { [styles.tapAnywhereEnabled]: isTapAnywhereEnabled })}>
        {isTapAnywhereEnabled && (
          <div>
            <div className={styles.poiInfoContainer}>
              <div className={styles.poiInfoHeader}>
                <Typography variant="text200bold">{name}</Typography>
                <Typography variant="text100" color="secondary">
                  {type_label}
                </Typography>
              </div>
              <div className={styles.coordinatesContainer}>
                <Typography variant="text100" color="secondary">
                  {_geoloc?.lat.toFixed(4)}, {_geoloc?.lng.toFixed(4)}
                </Typography>
                <IconButton
                  variant="ghost"
                  onClick={onClickCopyCoordinates}
                  title={intl.formatMessage({ defaultMessage: 'Copy coordinates' })}
                  icon={{ Component: isCopied ? Check : Copy, color: '--color-text-secondary' }}
                />
              </div>
            </div>
            <div className={styles.divider} />
          </div>
        )}
        <div className={isTapAnywhereEnabled ? styles.dropdownMenuItems : undefined}>
          <DropdownMenuItems options={options} />
        </div>
      </div>
    </PaddingAwarePopup>
  );
};

export default ActivePOIPopupOptions;
